// Timeline management
let timelineIntervals = new Map(); // Store active intervals by filename+stateMachine

/**
 * Creates a timeline with scheduled updates
 * @param {string} fileName - The Rive file name
 * @param {string} stateMachineName - The state machine name
 * @param {Object} riveInstance - The Rive instance
 * @param {Object} rive - The Rive library
 */
function createTimeline(fileName, stateMachineName, riveInstance, rive) {
    // Clear any existing timeline for this file/state machine
    const timelineKey = `${fileName}_${stateMachineName}`;
    if (timelineIntervals.has(timelineKey)) {
        clearInterval(timelineIntervals.get(timelineKey));
        timelineIntervals.delete(timelineKey);
    }
    
    console.log(`Creating timeline for ${fileName} with state machine ${stateMachineName}`);
    
    // Get the current Rive instance and inputs
    if (!riveInstance) {
        console.error('No Rive instance available for timeline');
        return;
    }
    
    try {
        const inputs = riveInstance.stateMachineInputs(stateMachineName);
        if (!inputs || inputs.length === 0) {
            console.log(`No inputs found for timeline in state machine "${stateMachineName}"`);
            return;
        }
        
        // Filter for number inputs only
        const numberInputs = inputs.filter(input => input.type === rive.StateMachineInputType.Number);
        
        if (numberInputs.length === 0) {
            console.log(`No number inputs found for timeline in state machine "${stateMachineName}"`);
            return;
        }
        
        console.log(`Found ${numberInputs.length} number inputs for timeline:`, numberInputs.map(input => input.name));
        
        // Create timeline sequences based on filename
        let timelineSequence = [];
        
        // Define different timeline sequences for different files
        if (/ls-audio_animation/.test(fileName)) {
            // Timeline for ls-audio_animation files
            timelineSequence = [
                { time: 0, updates: { 'position': 2} },
                { time: 500, updates: { 'rotation': 2 } },
                { time: 1000, updates: { 'position': 3 } },
                { time: 2000, updates: { 'position': 4 } },
                { time: 2500, updates: { 'scale': 2 } },
                { time: 2600, updates: { 'scale': 6 } },
                { time: 2700, updates: { 'scale': 1 } },
                { time: 2800, updates: { 'scale': 3 } },
                { time: 2900, updates: { 'scale': 0 } },
                { time: 3000, updates: { 'position': 5 } },
                { time: 4000, updates: { 'position': 6 } },
                { time: 5000, updates: { 'position': 7 } },
                { time: 6000, updates: { 'position': 8 } },
                { time: 6500, updates: { 'rotation': 3 } },
                { time: 6500, updates: { 'scale': 3 } },
                { time: 6700, updates: { 'scale': 1 } },
                { time: 6700, updates: { 'scale': 2 } },
                { time: 6900, updates: { 'scale': 0 } },
                { time: 7000, updates: { 'position': 9 } },
                { time: 7200, updates: { 'scale': 4 } },
                { time: 7300, updates: { 'scale': 6 } },
                { time: 7400, updates: { 'scale': 3 } },
                { time: 7500, updates: { 'scale': 5 } },
                { time: 7600, updates: { 'scale': 0 } },
                { time: 8000, updates: { 'position': 1 } },
            ];
        } else {
            console.warn('No timeline available for ' + fileName);
            throw { message: 'no_timeline', fileName };
        }
        
        // Start the timeline
        let currentStep = 0;
        const startTime = Date.now();
        
        const intervalId = setInterval(() => {
            const elapsedTime = Date.now() - startTime;
            
            // Find the appropriate step based on elapsed time
            // Look for the last step that should have been played by now
            let stepToPlay = -1;
            for (let i = 0; i < timelineSequence.length; i++) {
                if (timelineSequence[i].time <= elapsedTime % timelineSequence[timelineSequence.length - 1].time) {
                    stepToPlay = i;
                }
            }
            
            // If we found a step to play and it's different from the current step
            if (stepToPlay !== -1 && stepToPlay !== currentStep) {
                const step = timelineSequence[stepToPlay];
                
                console.log(`Timeline step ${stepToPlay} at ${elapsedTime}ms:`, step.updates);
                
                // Apply updates for this step
                Object.entries(step.updates).forEach(([inputName, value]) => {
                    // Find the input by name
                    const input = numberInputs.find(inp => inp.name === inputName);
                    if (input) {
                        // Update the input value directly
                        input.value = value;
                        console.log(`Timeline updated ${inputName} to ${value}`);
                        
                        // Also update any UI elements if they exist
                        // Look for input elements with class 'number-input' and find the one for this input
                        const numberInputElements = document.querySelectorAll('.number-input');
                        numberInputElements.forEach(element => {
                            // Check if this element corresponds to our input by checking the parent structure
                            const label = element.closest('.number-control')?.querySelector('.number-label');
                            if (label && label.textContent === inputName) {
                                element.value = value;
                            }
                        });
                    } else {
                        console.warn(`Timeline: Input "${inputName}" not found in state machine`);
                    }
                });
                
                // Update current step
                currentStep = stepToPlay;
            }
        }, 100); // 100ms check interval, but actual timing based on timelineSequence
        
        // Store the interval ID for cleanup
        timelineIntervals.set(timelineKey, intervalId);
        
        console.log(`Timeline started for ${fileName} with ${timelineSequence.length} steps`);
        
    } catch (error) {
        console.error('Error creating timeline:', error);
    }
}

/**
 * Stops timeline for a specific file/state machine
 * @param {string} fileName - The Rive file name
 * @param {string} stateMachineName - The state machine name
 */
function stopTimeline(fileName, stateMachineName) {
    const timelineKey = `${fileName}_${stateMachineName}`;
    if (timelineIntervals.has(timelineKey)) {
        clearInterval(timelineIntervals.get(timelineKey));
        timelineIntervals.delete(timelineKey);
        console.log(`Timeline stopped for ${fileName}_${stateMachineName}`);
    }
}

/**
 * Stops all timelines
 */
function stopAllTimelines() {
    timelineIntervals.forEach((intervalId, key) => {
        clearInterval(intervalId);
        console.log(`Timeline stopped for ${key}`);
    });
    timelineIntervals.clear();
}

/**
 * Creates timeline UI controls
 * @param {HTMLElement} container - Container element to add controls to
 * @param {string} fileName - The Rive file name
 * @param {string} stateMachineName - The state machine name
 * @param {Object} riveInstance - The Rive instance
 * @param {Object} rive - The Rive library
 */
function createTimelineControls(container, fileName, stateMachineName, riveInstance, rive) {
    // Check if a timeline schedule exists for this file
    // Try to create a timeline temporarily to see if it works
    let hasTimelineSchedule = true;
    
    try {
        // Test if a timeline can be created for this file
        const testTimelineInput = riveInstance.stateMachineInputs(stateMachineName);
        if (!testTimelineInput || testTimelineInput.length === 0) {
            hasTimelineSchedule = false;
        } else {
            // Special pattern check - we only support specific files in our timeline
            if (!/ls-audio_animation/.test(fileName)) {
                hasTimelineSchedule = false;
            }
        }
    } catch (error) {
        console.warn('Cannot create timeline for this file:', error);
        hasTimelineSchedule = false;
    }
    
    // If no timeline schedule exists, don't create controls
    if (!hasTimelineSchedule) {
        console.log('No timeline schedule available for this file, hiding controls');
        throw { message: 'no_timeline', fileName };
    }
    
    // If container is provided, don't create floating controls
    if (container) {
        return; // Let the container handle the UI creation
    }
    
    // Create timeline group - as a standalone panel (only when no container provided)
    const timelineGroup = document.createElement('div');
    timelineGroup.className = 'timeline-controls';
    
    // Apply styles for standalone panel
    timelineGroup.style.position = 'fixed';
    timelineGroup.style.bottom = '40px';
    timelineGroup.style.left = '50%';
    timelineGroup.style.transform = 'translateX(-50%)';
    timelineGroup.style.display = 'flex';
    timelineGroup.style.flexDirection = 'row';
    timelineGroup.style.gap = '10px';
    timelineGroup.style.padding = '10px 15px';
    timelineGroup.style.backgroundColor = 'rgba(240, 240, 240, 0.8)';
    timelineGroup.style.backdropFilter = 'blur(5px)';
    timelineGroup.style.borderRadius = '8px';
    timelineGroup.style.boxShadow = '0 2px 5px rgba(0, 0, 0, 0.2)';
    timelineGroup.style.zIndex = '1000';
    
    // Create play/pause toggle button
    const toggleButton = document.createElement('button');
    toggleButton.className = 'timeline-toggle-btn';
    toggleButton.style.padding = '6px 12px';
    toggleButton.style.border = 'none';
    toggleButton.style.borderRadius = '4px';
    toggleButton.style.color = 'white';
    toggleButton.style.cursor = 'pointer';
    toggleButton.style.fontWeight = 'bold';
    toggleButton.style.transition = 'background-color 0.2s';
    toggleButton.style.display = 'flex';
    toggleButton.style.alignItems = 'center';
    toggleButton.style.justifyContent = 'center';
    toggleButton.style.minWidth = '80px';
    
    // Play icon (Unicode triangle)
    const playIcon = document.createElement('span');
    playIcon.textContent = '▶';
    playIcon.style.marginRight = '5px';
    
    // Pause icon (Unicode double bars)
    const pauseIcon = document.createElement('span');
    pauseIcon.textContent = '⏸';
    pauseIcon.style.marginRight = '5px';
    pauseIcon.style.display = 'none';
    
    // Add icons to button
    toggleButton.appendChild(playIcon);
    toggleButton.appendChild(pauseIcon);
    toggleButton.appendChild(document.createTextNode('Play'));
    
    // Status indicator - created before we use it in updateButtonState
    const statusIndicator = document.createElement('div');
    statusIndicator.className = 'timeline-status';
    statusIndicator.style.width = '12px';
    statusIndicator.style.height = '12px';
    statusIndicator.style.borderRadius = '50%';
    statusIndicator.style.backgroundColor = '#ccc';
    statusIndicator.style.marginLeft = '10px';
    statusIndicator.style.display = 'flex';
    statusIndicator.style.alignSelf = 'center';
    
    // Function to update button state
    const updateButtonState = (isPlaying) => {
        if (isPlaying) {
            toggleButton.style.backgroundColor = '#f44336'; // Red when playing
            playIcon.style.display = 'none';
            pauseIcon.style.display = 'inline';
            toggleButton.lastChild.nodeValue = 'Pause';
            statusIndicator.style.backgroundColor = '#4CAF50';
        } else {
            toggleButton.style.backgroundColor = '#4CAF50'; // Green when paused
            playIcon.style.display = 'inline';
            pauseIcon.style.display = 'none';
            toggleButton.lastChild.nodeValue = 'Play';
            statusIndicator.style.backgroundColor = '#ccc';
        }
    };
    
    // For files with timeline support, start the timeline automatically
    // First check if a timeline is already active
    const isActive = isTimelineActive(fileName, stateMachineName);
    if (!isActive) {
        // Start the timeline automatically
        try {
            createTimeline(fileName, stateMachineName, riveInstance, rive);
            console.log('Timeline started automatically');
        } catch (error) {
            console.warn('Failed to start timeline automatically:', error);
        }
    }
    
    // Initialize button state - always set to "playing" since we auto-start
    updateButtonState(true);
    
    // Hover effects
    toggleButton.addEventListener('mouseenter', () => {
        if (isTimelineActive(fileName, stateMachineName)) {
            toggleButton.style.backgroundColor = '#d32f2f'; // Darker red
        } else {
            toggleButton.style.backgroundColor = '#45a049'; // Darker green
        }
    });
    
    toggleButton.addEventListener('mouseleave', () => {
        if (isTimelineActive(fileName, stateMachineName)) {
            toggleButton.style.backgroundColor = '#f44336'; // Red
        } else {
            toggleButton.style.backgroundColor = '#4CAF50'; // Green
        }
    });
    
    // Toggle click handler
    toggleButton.addEventListener('click', () => {
        const isActive = isTimelineActive(fileName, stateMachineName);
        
        if (isActive) {
            // If active, stop it
            stopTimeline(fileName, stateMachineName);
            console.log('Timeline stopped manually');
        } else {
            // If not active, start it
            createTimeline(fileName, stateMachineName, riveInstance, rive);
            console.log('Timeline started manually');
        }
        
        // Update button state
        updateButtonState(!isActive);
    });
    
    // Add elements to the timeline group
    timelineGroup.appendChild(toggleButton);
    timelineGroup.appendChild(statusIndicator);
    
    // Add timeline controls to document body, not the container
    document.body.appendChild(timelineGroup);
}

/**
 * Adds timeline controls to any page
 * @param {string} fileName - The Rive file name
 * @param {string} stateMachineName - The state machine name
 * @param {Object} riveInstance - The Rive instance
 * @param {Object} rive - The Rive library
 */
function addTimelineControlsToPage(fileName, stateMachineName, riveInstance, rive) {
    createTimelineControls(null, fileName, stateMachineName, riveInstance, rive);
}

/**
 * Checks if a timeline is active for a specific file/state machine
 * @param {string} fileName - The Rive file name
 * @param {string} stateMachineName - The state machine name
 * @returns {boolean} - Whether the timeline is active
 */
function isTimelineActive(fileName, stateMachineName) {
    const timelineKey = `${fileName}_${stateMachineName}`;
    return timelineIntervals.has(timelineKey);
}

// Export timeline functions
export {
    createTimeline,
    stopTimeline,
    stopAllTimelines,
    createTimelineControls,
    addTimelineControlsToPage,
    isTimelineActive
}; 